import Breadcrumbs from "@/components/Breadcrumbs";
import CraftPageEditor from "@/components/PageEditor";
import { PageService } from "@/services/admin-services";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import { PageType } from "@/services/shared-models";
import { SerializedNodes } from "@craftjs/core";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PageConfigurator from "./components/PageConfigurator";
import { PageConfiguration } from "./components/PageConfigurator/PageConfigurator";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Content",
        to: "../..",
    },
    {
        title: "Pages",
        to: "..",
    },
];

const Editor = () => {
    const { identifier } = useParams();

    const [loading, setLoading] = useState(true);

    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);
    const [languages, setLanguages] = useState<Array<Language>>([]);
    const [pageConfiguration, setPageConfiguration] = useState<PageConfiguration>();

    const [data, setData] = useState<string | SerializedNodes>();

    const pageService = useMemo(() => new PageService("int"), []);

    useEffect(() => {
        LocaleService.get().then((regions) => parseLanguages(regions));
    }, []);

    useEffect(() => {
        if (!identifier) {
            setBreadcrumbTrail([...breadcrumbs, { title: "New page", to: "" }]);
            setPageConfiguration({
                title: "New page",
                slug: "",
                language: "int-en",
                pageType: PageType.Content,
                parent: "",
                parentOrder: 0,
                inverseOrder: false,
                description: "",
                hidden: false,
                comments: false,
            });
            setLoading(false);
        } else {
            pageService.getPage(identifier).then((page) => {
                setBreadcrumbTrail([...breadcrumbs, { title: page.title, to: "" }]);
                setPageConfiguration({
                    title: page.title,
                    slug: page.slug,
                    language: page.language,
                    pageType: page.pageType,
                    parent: page.parentPageId || "",
                    parentOrder: page.parentOrder,
                    inverseOrder: page.inverseOrder || false,
                    description: page.description,
                    hidden: page.hidden,
                    comments: page.allowComments || false,
                });
                setData(JSON.parse(page.content));
                setLoading(false);
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identifier]);

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({ ...language, slug: `int-${split[1]}`, regionLabel: "International" });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    if (loading || !pageConfiguration || !languages || languages.length === 0)
        return (
            <Box>
                <Breadcrumbs trail={breadcrumbTrail} />

                <Grid container spacing={4} p={{ xs: 2, md: 4 }}>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Box sx={{ display: "flex", height: 400, alignItems: "center", flexDirection: "column" }}>
                            <Box sx={{ margin: "auto" }}>
                                <Typography>Loading data...</Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        );

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <CraftPageEditor data={data}>
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        zIndex: 100,
                        backgroundColor: "rgb(255, 255, 255)",
                        p: (theme) => theme.spacing(4, 0),
                    }}
                >
                    <PageConfigurator configuration={pageConfiguration} languages={languages} />
                </Box>

                <Divider />
            </CraftPageEditor>
        </Box>
    );
};

export default Editor;
